import React from 'react';

import {
  PURCHASE_CLICK,
  PURCHASE_CREDITS_LABEL,
} from '@editor/global/analytics/events';
import { useCreditStore } from '@editor/stores/creditStore';
import { usePromotionStore } from '@editor/stores/promotionStore';
import { useUserStore } from '@editor/stores/userStore';
import { GENERATING_ERRORS, GENERATING_ERROR_MESSAGES } from '@editor/types/Ai';
import {
  getUserCreditsInfo,
  userHasEnoughCredits,
} from '@editor/utils/credits';
import analytics from '@repo/analytics';
import { SubscriptionPlan } from '@repo/domain/user';
import { subscriptionApi } from '@repo/subscription/api';
import { buildWebsiteUrl } from '@repo/utils/url';

import { UpgradeModalContentType } from '../UpgradeFlowModal/types';

import { ImageGenerationModel } from './types';

/**
 * Returns the modal name to show for upgrading the AI panel based on the user's plan and credits.
 * @param userPlan - The user's subscription plan.
 * @param extraCredits - The number of extra credits the user has.
 * @param remainingAmount - The remaining amount of monthly credits.
 * @returns The modal name to show for upgrading the AI panel.
 */
export const getUpgradeModalAiPanel = (
  userPlan: SubscriptionPlan | undefined,
  totalCredits: number
): UpgradeModalContentType | null => {
  let modalToShow: UpgradeModalContentType | null =
    'upgradePlanWithMoreCreditsAIPanel';
  if (!userPlan && totalCredits !== 0) {
    modalToShow = 'upgradePlanAsFreeUserAiPanel';
  } else if (!userPlan && totalCredits === 0) {
    modalToShow = 'upgradePlanWithMoreCreditsAsFreeUserAiPanel';
  } else if (userPlan === 'PRO' && totalCredits === 0) {
    modalToShow = 'upgradePlanWithMoreCreditsAsProUserAiPanel';
  } else if (userPlan === 'EXPERT' && totalCredits === 0) {
    modalToShow = 'buyMoreCreditsAsExpertUserAiPanel';
  }

  return modalToShow;
};

/**
 * Handles the purchase of credits.
 * Creates an order and then redirects to the payment page.
 */
export const handleBuyCredits = async (): Promise<void> => {
  // Get the list of products available for the user
  const products = useCreditStore.getState().products;
  if (products.length > 0) {
    analytics.track(PURCHASE_CLICK, { label: PURCHASE_CREDITS_LABEL });
    const selectedProduct = products[0];
    // Create the order in the api
    const response = await subscriptionApi.createOrder([
      {
        id: selectedProduct.id,
        quantity: 1,
        // TODO: remove ts-ignore once BE is merged https://app.clickup.com/t/86c2ztc52
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        amount: selectedProduct.price.amount,
        currency: selectedProduct.price.currency,
      },
    ]);
    // Redirect to the payment page
    if ('url' in response) {
      window.location.href = response.url;
    }
  }
};

/**
 * Alerts the user if they do not have enough credits.
 * Based on the plan, we either show the upgrade modal or the buy credits modal.
 * @param cost - The cost of the operation.
 * @param generatingModel - The generating model.
 * @param trackingLabel - The tracking label.
 * @returns Whether the user has enough credits.
 */
export const alertUserIfNotEnoughCredits = (
  cost?: number,
  generatingModel?: ImageGenerationModel,
  trackingLabel?: string
): { hasEnoughCredits: boolean } => {
  const user = useUserStore.getState().user;
  const showUpgradeModal = usePromotionStore.getState().showUpgradeModal;

  // If no cost is provided, any credits is enough
  if (!cost) {
    return { hasEnoughCredits: true };
  }

  if (user && !userHasEnoughCredits(user, cost)) {
    const { total } = getUserCreditsInfo(user);
    const modalToShow = getUpgradeModalAiPanel(user?.plan, total);

    if (modalToShow) {
      let onClickCallback = undefined;
      if (modalToShow === 'buyMoreCreditsAsExpertUserAiPanel') {
        onClickCallback = handleBuyCredits;
      }
      showUpgradeModal(
        modalToShow,
        {
          textToImageModelVersion:
            generatingModel ?? ImageGenerationModel.Turbo,
          trackingLabel: trackingLabel ?? 'upgradePlanWithMoreCreditsAIPanel',
        },
        onClickCallback
      );
    }
    return { hasEnoughCredits: false };
  }
  return { hasEnoughCredits: true };
};

/**
 * Returns the error modal data based on the error type.
 * @param error - The error type.
 * @returns The error modal data or null if there is no error.
 */
export const getImageGenRequestErrorModalData = (
  error: string | null
): {
  title: string;
  message: React.ReactNode;
} | null => {
  if (!error) {
    return null;
  }
  if (error === GENERATING_ERRORS.UNSAFE_PROMPT) {
    return {
      title: 'Prompt blocked',
      message: (
        <>
          It looks like the prompt you entered doesn't comply with our{' '}
          <a
            href={buildWebsiteUrl('/help/design/ai-usage-rules')}
            rel="noreferrer"
            target="_blank"
          >
            <b>AI Usage Rules</b>
          </a>
          .
        </>
      ),
    };
  }

  if (error === GENERATING_ERRORS.PROMPT_TOO_LONG) {
    return {
      title: 'Prompt too long',
      message: (
        <>{GENERATING_ERROR_MESSAGES[GENERATING_ERRORS.PROMPT_TOO_LONG]}</>
      ),
    };
  }

  if (error === GENERATING_ERRORS.TOO_MANY_REQUESTS) {
    return {
      title: 'Rate limit reached',
      message: (
        <>
          Rate limits ensure fair access and a smooth experience for all Kittl
          AI users. Please try again shortly.
        </>
      ),
    };
  }

  return {
    title: 'Server issue',
    message: (
      <>
        Sorry, we're experiencing a technical issue.
        <br />
        Please try again shortly.
      </>
    ),
  };
};

export const isUserEmailFromKittl = (email: string | undefined): boolean => {
  return !!email && email.endsWith('@kittl.com');
};
